import { isNullOrUndefined } from "base/utils";
import { RecipeContext } from "components/recipe/providers/RecipeProvider";
import {
  validateIsRequired,
  validatePercentage,
  validatePositiveNumber,
} from "base/validation";
import FormTextField from "base/FormTextField";
import ImageUploader from "base/UploadImage";
import React, { useContext, useEffect, useState } from "react";

const AddRecipeBaseInfo = ({ recipe, onRecipeUpdate, validationMessages }) => {
  const { macros } = useContext(RecipeContext);
  const [recipeMacros, setMacros] = useState([]);

  const handleRecipeUpdate = (field, value) => {
    let isValid = true;
    let messages = [];
    let fieldsToValidate = [
      "recipe_name",
      "serving_size",
      "num_of_servings",
      "macros",
    ];

    fieldsToValidate.forEach((f) => {
      switch (f) {
        case "recipe_name":
        case "serving_size":
          isValid = validateIsRequired(f == field ? value : recipe[f]);
          break;
        case "num_of_servings":
          let val = f == field ? value : recipe[f];
          messages = [validateIsRequired(val), validatePositiveNumber(val)];
          isValid = messages.find((v) => typeof v == "string") || true;
          break;
        case "macros":
          messages = recipeMacros.flatMap(
            (mac) => mac.errorMessage || mac.percentageErrorMessage || [],
          );
          isValid = messages.find((v) => typeof v == "string") || true;
          break;
      }
      validationMessages(f, isValid, "baseInfo");
    });
    onRecipeUpdate(field, value);

    let isArray = Array.isArray(value);
    if ((isArray && value?.length > 0) || (!isArray && value)) {
      moveToNextField(field);
    }
  };

  const moveToNextField = (currentField) => {
    const uploadImageElement = document.getElementById("upload-container");
    const servingsElement = document.getElementById("servings-element");
    const servingSizeElement = document.getElementById("serving-size-element");
    const caloriesElement = document.getElementById("calorie-element");
    const timingContainerFirstInputElement = document.querySelector(
      "#timing-container input",
    );
    const macroCarbContainerElement = document.querySelector(
      "#Carbs-macro-container input",
    );
    const macroFatContainerElement = document.querySelector(
      "#Fat-macro-container input",
    );
    const macroProteinContainerElement = document.querySelector(
      "#Protein-macro-container input",
    );

    let nextField = null;
    switch (currentField) {
      case "recipe_name":
        nextField = uploadImageElement;
        break;
      case "images":
        nextField = servingsElement;
        break;
      case "num_of_servings":
        nextField = servingSizeElement;
        break;
      case "serving_size":
        nextField = caloriesElement;
        break;
      case "calories":
        nextField = timingContainerFirstInputElement;
        break;
      case "cook_bake":
        nextField = macroCarbContainerElement;
        break;
      case "Carbs":
        nextField = macroFatContainerElement;
        break;
      case "Fat":
        nextField = macroProteinContainerElement;
        break;
    }
    if (nextField) nextField.focus();
  };

  const updatePrepTime = (val) => {
    const prep = parseInt(val) || 0;
    const cook = parseInt(recipe.cook_time) || 0;
    onRecipeUpdate({ prep_time: prep, total_time: prep + cook });
  };

  const updateCookTime = (field, val) => {
    const cook = parseInt(val) || 0;
    const prep = parseInt(recipe.prep_time) || 0;
    onRecipeUpdate({ cook_time: cook, total_time: prep + cook });
    if (val) moveToNextField(field);
  };

  const updateMacros = (val, field, macroToUpdate) => {
    let macro = recipeMacros.find((m) => m.label == macroToUpdate.label);
    macro[field] = val;
    //set error messaging for user to see below input
    if (field == "percentage")
      validatePercentage(macro, "percentageErrorMessage");
    if (field == "grams") validatePositiveNumber(macro, "grams");
    setMacros([...recipeMacros]);
    handleRecipeUpdate("macros", [...recipeMacros]);
    if (val && field == "percentage") moveToNextField(macroToUpdate.label);
  };

  useEffect(() => {
    let macrosWithIds = macros.map((m) => {
      return { grams: 0, percentage: 0, ...m };
    });
    setMacros(macrosWithIds);
  }, [macros]);

  return (
    <section className="Column add-recipe-accordion-section">
      <FormTextField
        id="name-element"
        fieldLabel="Recipe Name"
        onClear={(val) => handleRecipeUpdate("recipe_name", val)}
        onBlur={(val) => handleRecipeUpdate("recipe_name", val)}
        placeholder="What would you like to call your recipe?"
        required
        tabIndex={-1}
        value={recipe.recipe_name}
      />
      <ImageUploader
        id="upload-container"
        cloudName="jessicadever1"
        uploadPreset="blogFreeRecipe"
        maxImages={5}
        onImageUpload={(images) => handleRecipeUpdate("images", images)}
        tabIndex={-1}
      />
      <FormTextField
        id="servings-element"
        fieldLabel="Total Servings"
        fieldType="number"
        onBlur={(val) => handleRecipeUpdate("num_of_servings", val)}
        placeholder="How many servings will this recipe make?"
        required
        tabIndex={-1}
        value={recipe.num_of_servings}
      />
      <FormTextField
        id="serving-size-element"
        fieldLabel="Serving Size"
        onBlur={(val) => handleRecipeUpdate("serving_size", val)}
        placeholder="What is the size of each serving?"
        required
        tabIndex={-1}
        value={recipe.serving_size}
      />
      <FormTextField
        id="calorie-element"
        fieldLabel="Calories"
        fieldType="number"
        onBlur={(val) => handleRecipeUpdate("calories", val)}
        placeholder="How many calories per serving?"
        tabIndex={-1}
        value={recipe.calories}
      />
      <div id="timing-container" className="Row w-100">
        <FormTextField
          fieldLabel="Prep Time (minutes)"
          fieldType="number"
          onChange={updatePrepTime}
          placeholder="Prep Time (minutes)"
          value={recipe.prep_time}
        />
        <FormTextField
          className="mx-8"
          fieldLabel="Cook/Bake Time (min)"
          fieldType="number"
          onChange={(val) => updateCookTime("cook_bake", val)}
          placeholder="Cook/Bake Time (min)"
          value={recipe.cook_time}
        />
        <FormTextField
          disabled
          fieldLabel="Total Time"
          fieldType="number"
          placeholder="Total Time"
          value={recipe.total_time}
        />
      </div>
      {recipeMacros.map((macro) => {
        return (
          <div
            id={`${macro.label}-macro-container`}
            key={`${macro.label}-macro-container`}
            className="Row w-100"
          >
            <div className="w-33 px-4">{macro.label}</div>
            <FormTextField
              className="mx-8"
              error={!isNullOrUndefined(macro?.errorMessage)}
              fieldLabel="Grams"
              fieldSubText={macro?.errorMessage}
              fieldType="number"
              onBlur={(val) => updateMacros(val, "grams", macro)}
              placeholder={`Grams of ${macro.label}`}
              value={macro.grams}
            />
            <FormTextField
              error={!isNullOrUndefined(macro?.percentageErrorMessage)}
              fieldLabel="Percentage"
              fieldSubText={macro?.percentageErrorMessage}
              fieldType="number"
              onBlur={(val) => updateMacros(val, "percentage", macro)}
              placeholder={`${macro.label} percentage`}
              value={macro.percentage}
            />
          </div>
        );
      })}
    </section>
  );
};

export default AddRecipeBaseInfo;
